import { type CreateLead, type LeadCard, LEAD_SOURCES, PROCEDURE_AREAS } from '@dnx/contracts';
import { formatBrPhoneInput, normalizeBrPhone } from '@dnx/domain';
import { Alert, Button, Dialog, Field, Input, Select } from '@dnx/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { AREA_LABELS, SOURCE_LABELS } from '../lib/labels.js';

/** Lead que não chegou pelo WhatsApp: ligação, balcão, indicação, evento. */
export function NewLeadDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<CreateLead>({ name: '', phone: '', source: 'phone', procedureArea: null, note: '' });
  const [touched, setTouched] = useState(false);
  const phoneOk = normalizeBrPhone(f.phone) !== null;
  const valid = f.name.trim().length >= 2 && phoneOk;
  const create = useMutation({
    mutationFn: () => api<LeadCard>('/crm/leads', { method: 'POST', body: { ...f, note: f.note?.trim() || undefined } }),
    onSuccess: (lead) => {
      void qc.invalidateQueries({ queryKey: ['crm'] });
      setF({ name: '', phone: '', source: 'phone', procedureArea: null, note: '' });
      setTouched(false);
      onCreated(lead.id);
    },
  });
  return (
    <Dialog open={open} onClose={onClose} title="Novo lead" description="Para quem chegou por ligação, balcão ou indicação. Quem escreve no WhatsApp entra sozinho.">
      <form className="grid gap-4 p-5" noValidate onSubmit={(e) => { e.preventDefault(); setTouched(true); if (valid) create.mutate(); }}>
        {create.error && <Alert tone="danger">{create.error.message}</Alert>}
        <Field label="Nome" error={touched && f.name.trim().length < 2 ? 'Informe o nome' : undefined}>
          <Input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label="Celular com DDD" error={touched && !phoneOk ? 'Celular inválido' : undefined}>
          <Input type="tel" inputMode="numeric" value={f.phone} placeholder="(11) 91234-5678" onChange={(e) => setF({ ...f, phone: formatBrPhoneInput(e.target.value) })} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Origem">
            <Select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value as CreateLead['source'] })}>
              {LEAD_SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
            </Select>
          </Field>
          <Field label="Interesse">
            <Select value={f.procedureArea ?? ''} onChange={(e) => setF({ ...f, procedureArea: (e.target.value || null) as CreateLead['procedureArea'] })}>
              <option value="">Não informado</option>
              {PROCEDURE_AREAS.map((a) => <option key={a} value={a}>{AREA_LABELS[a]}</option>)}
            </Select>
          </Field>
        </div>
        <Field label="Nota (opcional)">
          <textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} rows={3}
            className="w-full resize-none rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg focus:border-primary" />
        </Field>
        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button type="submit" loading={create.isPending}>Criar lead</Button>
        </div>
      </form>
    </Dialog>
  );
}
